import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AlertModal } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { getEmploymentTypes, deleteEmploymentType } from '@/services/employmentTypeService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { EmploymentTypeDto } from '@/types/employmentType';
import type { FilterFieldConfig } from '@/types/filters';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import ViewEmploymentTypeModal from '@features/employment-types/ViewEmploymentTypeModal';
import CreateEditEmploymentTypeModal from '@features/employment-types/CreateEditEmploymentTypeModal';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/employment-types/')({
  validateSearch: validateTableSearch,
  component: EmploymentTypesPage,
});

// ============================================
// HELPERS
// ============================================

function StatusBadge({ status }: { status: EmploymentTypeDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{status}</span>;
}

function BoolBadge({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={`${styles.badge} ${value ? styles.badgeActive : styles.badgeInactive}`}>
      {value ? t('K_YES', 'Yes') : t('K_NO', 'No')}
    </span>
  );
}

function humanize(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

// ============================================
// COMPONENT
// ============================================

function EmploymentTypesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Filter fields ordered to match table columns:
  // Name, Identifier, Tax Type, Payroll, Statutory Benefits, Invoice Workflow, Overridable, Status
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'taxType',
        label: t('K_FILTER_TAX_TYPE', 'Tax Type'),
        type: 'select',
        options: [
          { label: t('K_OPTION_PAYE', 'PAYE'), value: 'PAYE' },
          { label: t('K_OPTION_NONE', 'None'), value: 'NONE' },
          { label: t('K_OPTION_WITHHOLDING_TAX', 'Withholding Tax'), value: 'WITHHOLDING_TAX' },
        ],
      },
      {
        fieldKey: 'payroll',
        label: t('K_FILTER_PAYROLL', 'Payroll'),
        type: 'toggle',
      },
      {
        fieldKey: 'statutoryBenefits',
        label: t('K_FILTER_STATUTORY_BENEFITS', 'Statutory Benefits'),
        type: 'toggle',
      },
      {
        fieldKey: 'invoiceWorkflow',
        label: t('K_FILTER_INVOICE_WORKFLOW', 'Invoice Workflow'),
        type: 'select',
        options: [
          { label: t('K_OPTION_NOT_APPLICABLE', 'Not Applicable'), value: 'NOT_APPLICABLE' },
          { label: t('K_OPTION_REQUIRED', 'Required'), value: 'REQUIRED' },
        ],
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/employment-types',
    filtersParam,
    fields: filterFields,
  });

  // Modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<EmploymentTypeDto | null>(null);
  const [editItem, setEditItem] = useState<EmploymentTypeDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<EmploymentTypeDto | null>(null);
  const [auditType, setAuditType] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);

  const handleView = useCallback((item: EmploymentTypeDto) => setViewItem(item), []);
  const handleCloseView = useCallback(() => setViewItem(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      setEditItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleDeleteFromView = useCallback(() => {
    if (viewItem) {
      setDeleteItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      setAuditType({ id: viewItem.id, name: viewItem.name });
      setViewItem(null);
    }
  }, [viewItem]);

  const handleCloseEdit = useCallback(() => setEditItem(null), []);

  const handleCloseDelete = useCallback(() => setDeleteItem(null), []);

  const deleteMutation = useMutation({
    mutationFn: deleteEmploymentType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employmentTypes'] });
      setDeleteItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (deleteItem) {
      deleteMutation.mutate(deleteItem.id);
    }
  }, [deleteItem, deleteMutation]);

  const columns = useMemo<DataTableColumn<EmploymentTypeDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>{item.name || '-'}</span>
        ),
      },
      {
        id: 'taxType',
        header: t('K_COLUMN_TAX_TYPE', 'Tax Type'),
        cell: (item) => humanize(item.taxType),
      },
      {
        id: 'payroll',
        header: t('K_COLUMN_PAYROLL', 'Payroll'),
        cell: (item) => <BoolBadge value={item.payroll} />,
      },
      {
        id: 'statutoryBenefits',
        header: t('K_COLUMN_STATUTORY_BENEFITS', 'Statutory Benefits'),
        cell: (item) => <BoolBadge value={item.statutoryBenefits} />,
      },
      {
        id: 'invoiceWorkflow',
        header: t('K_COLUMN_INVOICE_WORKFLOW', 'Invoice Workflow'),
        cell: (item) => humanize(item.invoiceWorkflow),
      },
      {
        id: 'invoiceWorkflowOverridable',
        header: t('K_COLUMN_OVERRIDABLE', 'Overridable'),
        cell: (item) => <BoolBadge value={item.invoiceWorkflowOverridable} />,
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
    ],
    [t],
  );

  const rowActions = useCallback((item: EmploymentTypeDto) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('EMPLOYMENT_TYPES_UPDATE') && item.status !== 'INACTIVE') {
      actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => setEditItem(item) });
    }
    if (hasPermission('EMPLOYMENT_TYPES_DELETE') && item.status === 'ACTIVE') {
      actions.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setDeleteItem(item) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => setAuditType({ id: item.id, name: item.name }),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/employment-types',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/employment-types',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/employment-types',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['employmentTypes', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getEmploymentTypes({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: EmploymentTypeDto) => item.id, []);

  const handleRowClick = useCallback(
    (item: EmploymentTypeDto) => handleView(item),
    [handleView],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_EMPLOYMENT_TYPES', 'No employment types yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_EMPLOYMENT_TYPE', 'Get started by creating your first employment type.')}
          </p>
          {hasPermission('EMPLOYMENT_TYPES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_EMPLOYMENT_TYPE', 'Create employment type')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_EMPLOYMENT_TYPES', 'Employment Types')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_EMPLOYMENT_TYPES', 'Manage employment type configurations')}</p>
        </div>

        {hasPermission('EMPLOYMENT_TYPES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_EMPLOYMENT_TYPE', 'Create employment type')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_EMPLOYMENT_TYPES', 'Loading employment types...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Employment types list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      {/* View Modal */}
      <ViewEmploymentTypeModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        data={viewItem}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
        onAudit={handleAuditFromView}
      />

      {/* Create Modal */}
      <CreateEditEmploymentTypeModal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
      />

      {/* Edit Modal */}
      <CreateEditEmploymentTypeModal
        isOpen={editItem !== null}
        onClose={handleCloseEdit}
        editData={editItem}
      />

      {/* Deactivate Confirmation */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_EMPLOYMENT_TYPE', 'Deactivate Employment Type')}
        description={t('K_CONFIRM_DEACTIVATE_EMPLOYMENT_TYPE', 'Are you sure you want to deactivate "{name}"? The employment type will no longer be active.').replace('{name}', deleteItem?.name ?? '')}
        isOpen={deleteItem !== null}
        onClose={handleCloseDelete}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />

      {/* Audit Trail Modal */}
      <AuditTrailModal
        open={auditType !== null}
        onClose={() => setAuditType(null)}
        filter={{ resourceType: AUDIT_RESOURCE.EMPLOYMENT_TYPE, resourceId: auditType?.id ?? '' }}
        subject={auditType?.name}
      />
    </div>
  );
}
